import { ApiError } from "api/client";
import { subscribeToNewsletter } from "api/newsletter";
import { useBrand } from "hooks/useBrand";
import React, { type ChangeEvent, type FormEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Paths from "routes/Paths";

import type { NewsletterFormStatus } from "./landingPage.types";

import styles from "./NewsletterForm.module.css";

const NewsletterForm = () => {
	const { t } = useTranslation(["landingPage", "common"]);
	const { brand } = useBrand();
	const [email, setEmail] = useState("");
	const [status, setStatus] = useState<NewsletterFormStatus>("idle");
	const [message, setMessage] = useState("");

	const onEmailChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
		setEmail(event.target.value);
	}, []);

	const onSubmit = useCallback(
		async (event: FormEvent<HTMLFormElement>): Promise<void> => {
			event.preventDefault();

			const trimmedEmail = email.trim();
			if (!trimmedEmail || status === "submitting") {
				return;
			}

			setStatus("submitting");
			setMessage("");

			try {
				await subscribeToNewsletter(trimmedEmail);
				setStatus("success");
				setMessage(t("landingPage:footer.newsletter.success"));
				setEmail("");
			} catch (error) {
				setStatus("error");
				setMessage(error instanceof ApiError ? error.message : t("common:genericError"));
			}
		},
		[email, status, t]
	);

	return (
		<div className={styles.root}>
			<form className={styles.subscribeForm} onSubmit={onSubmit}>
				<input
					aria-label={t("landingPage:footer.newsletter.emailLabel")}
					disabled={status === "submitting"}
					onChange={onEmailChange}
					placeholder={t("landingPage:footer.newsletter.emailPlaceholder")}
					type="email"
					value={email}
					required
				/>
				<button disabled={status === "submitting"} type="submit">
					{status === "submitting"
						? t("landingPage:footer.newsletter.submitting")
						: t("landingPage:footer.newsletter.submit")}
				</button>
			</form>
			<p className={styles.consentNote}>
				{t("landingPage:footer.newsletter.consentNote", { brand })}{" "}
				<Link to={Paths.newsletterUnsubscribe}>{t("landingPage:footer.newsletter.unsubscribeLink")}</Link>
			</p>
			{message ? (
				<p className={status === "error" ? styles.error : styles.success} role="status">
					{message}
				</p>
			) : null}
		</div>
	);
};

export default NewsletterForm;
